import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  HelpCircle, 
  AlertCircle, 
  CheckCircle2, 
  Loader2,
  Lightbulb,
  TrendingUp,
  FileText
} from "lucide-react";
import { Slider } from "@/components/ui/slider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Field help tooltip component
const FieldHelp = ({ helpData }) => {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button 
            type="button" 
            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors ml-2"
            onClick={(e) => e.preventDefault()}
          >
            <HelpCircle className="w-3 h-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent 
          side="right" 
          className="max-w-md p-4 bg-white border border-slate-200 shadow-lg"
        >
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-sm text-slate-900 mb-1">{helpData.title}</h4>
                <p className="text-xs text-slate-600 mb-2">{helpData.description}</p>
              </div>
            </div>
            
            <div className="border-t border-slate-200 pt-2">
              <p className="text-xs font-medium text-slate-700 mb-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                Impact:
              </p>
              <ul className="space-y-1 ml-4">
                {helpData.impact.map((item, idx) => (
                  <li key={idx} className="text-xs text-slate-600 list-disc">{item}</li>
                ))}
              </ul>
            </div>
            
            {helpData.recommendation && (
              <div className="bg-green-50 border border-green-200 rounded p-2">
                <p className="text-xs font-medium text-green-800 mb-1">✓ Recommendation:</p>
                <p className="text-xs text-green-700">{helpData.recommendation}</p>
              </div>
            )}
            
            {helpData.example && (
              <div className="bg-slate-50 border border-slate-200 rounded p-2">
                <p className="text-xs font-medium text-slate-700 mb-1 flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Example:
                </p>
                <p className="text-xs text-slate-600 italic">{helpData.example}</p>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const PromptEditDialog = ({ open, onClose, prompt, onSave }) => {
  const [features, setFeatures] = useState(prompt.features);
  const [changeReason, setChangeReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);

  useEffect(() => {
    setFeatures(prompt.features);
  }, [prompt]);

  const handleFeatureChange = (field, value) => {
    setFeatures(prev => ({
      ...prev,
      [field]: value
    }));
    setValidation(null); // Clear validation when user makes changes
  };

  const handleValidate = async () => {
    setValidating(true);
    
    try {
      const response = await axios.post(
        `${API}/admin/prompts/${prompt.id}/validate`,
        { features },
        { withCredentials: true }
      );
      
      setValidation(response.data.validation);
      
      if (response.data.validation.valid) {
        toast.success("Validation passed");
      } else {
        toast.warning("Validation found issues");
      }
    } catch (error) {
      console.error("Validation error:", error);
      toast.error("Failed to validate");
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    // Validate first
    if (!validation || !validation.valid) {
      toast.error("Please validate your changes first");
      return;
    }

    setLoading(true);
    
    try {
      await onSave(features, changeReason);
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = JSON.stringify(features) !== JSON.stringify(prompt.features);
  const fieldHelp = prompt.field_help || {};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Edit Prompt Features - {prompt.category.replace('_', ' ')}
          </DialogTitle>
          <DialogDescription>
            Customize editable parameters. Base system prompts are locked for stability.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Locked Base Prompt Info */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-4 h-4 text-slate-600" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-slate-900 mb-1">Base Prompt is Locked</p>
                <p className="text-slate-600 text-xs">
                  System instructions, output schema, and scoring logic are read-only to maintain consistency and stability. 
                  Only feature parameters below can be modified.
                </p>
              </div>
            </div>
          </div>

          {/* Temperature */}
          <div className="space-y-2">
            <Label htmlFor="temperature" className="flex items-center">
              Temperature
              {fieldHelp.temperature && <FieldHelp helpData={fieldHelp.temperature} />}
            </Label>
            <div className="flex items-center gap-4">
              <Slider
                id="temperature"
                min={0}
                max={1}
                step={0.1}
                value={[features.temperature]}
                onValueChange={([value]) => handleFeatureChange('temperature', value)}
                className="flex-1"
              />
              <Input
                type="number"
                value={features.temperature}
                onChange={(e) => handleFeatureChange('temperature', parseFloat(e.target.value))}
                min={0}
                max={1}
                step={0.1}
                className="w-20"
              />
            </div>
            <p className="text-xs text-slate-500">
              Lower = more deterministic, Higher = more creative (Range: 0.0 - 1.0)
            </p>
          </div>

          {/* Max Tokens */}
          <div className="space-y-2">
            <Label htmlFor="max_tokens" className="flex items-center">
              Max Tokens
              {fieldHelp.max_tokens && <FieldHelp helpData={fieldHelp.max_tokens} />}
            </Label>
            <Input
              id="max_tokens"
              type="number"
              value={features.max_tokens}
              onChange={(e) => handleFeatureChange('max_tokens', parseInt(e.target.value))}
              min={64}
              max={4000}
            />
            <p className="text-xs text-slate-500">
              Maximum response length in tokens (Range: 64 - 4000)
            </p>
          </div>

          {/* Response Style */}
          <div className="space-y-2">
            <Label htmlFor="response_style" className="flex items-center">
              Response Style
              {fieldHelp.response_style && <FieldHelp helpData={fieldHelp.response_style} />}
            </Label>
            <Select
              value={features.response_style}
              onValueChange={(value) => handleFeatureChange('response_style', value)}
            >
              <SelectTrigger id="response_style">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="concise">Concise - Brief and to the point</SelectItem>
                <SelectItem value="detailed">Detailed - Comprehensive and elaborate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Confidence Threshold */}
          <div className="space-y-2">
            <Label htmlFor="confidence_threshold" className="flex items-center">
              Confidence Threshold
              {fieldHelp.confidence_threshold && <FieldHelp helpData={fieldHelp.confidence_threshold} />}
            </Label>
            <div className="flex items-center gap-4">
              <Slider
                id="confidence_threshold"
                min={0}
                max={1}
                step={0.05}
                value={[features.confidence_threshold]}
                onValueChange={([value]) => handleFeatureChange('confidence_threshold', value)}
                className="flex-1"
              />
              <div className="w-20 text-right font-medium text-slate-900">
                {(features.confidence_threshold * 100).toFixed(0)}%
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Minimum confidence to include data (Range: 0% - 100%)
            </p>
          </div>

          {/* Change Reason */}
          <div className="space-y-2 pt-4 border-t border-slate-200">
            <Label htmlFor="change_reason">Change Reason (Optional)</Label>
            <Textarea
              id="change_reason"
              placeholder="Describe why you're making these changes..."
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              rows={3}
            />
            <p className="text-xs text-slate-500">
              Document your changes for version history
            </p>
          </div>

          {/* Validation Results */}
          {validation && (
            <div className={`border rounded-lg p-4 ${
              validation.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {validation.valid ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-medium text-sm ${
                    validation.valid ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {validation.valid ? 'Validation Passed ✓' : 'Validation Failed'}
                  </p>
                  
                  {validation.errors && validation.errors.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {validation.errors.map((error, idx) => (
                        <li key={idx} className="text-xs text-red-700">• {error}</li>
                      ))}
                    </ul>
                  )}
                  
                  {validation.warnings && validation.warnings.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {validation.warnings.map((warning, idx) => (
                        <li key={idx} className="text-xs text-amber-700">⚠ {warning}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={handleValidate}
            disabled={!hasChanges || validating}
          >
            {validating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Validating...
              </>
            ) : (
              'Validate Changes'
            )}
          </Button>
          
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={loading || !validation || !validation.valid || !hasChanges}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PromptEditDialog;
